/**
 * AICoachTab - AI-powered training plan generation
 *
 * Contains:
 * - Prerequisites checklist
 * - Generate plan button
 * - Plan preview and confirmation
 */

import {
  AutoAwesome as AIIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckIcon,
  NavigateNext as NavIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import type { TrainingPlan, UserProfile } from '@shared/types';
import React, { useCallback, useEffect, useState } from 'react';
import apiClient from '../../../../services/api';
import { logger } from '../../../../utils/logger';
import { getTotalPlannedDistance } from '../../../../utils/planUtils';
import PlanPreviewDialog from '../../PlanPreviewDialog';

interface AICoachTabProps {
  profile: UserProfile;
  onUpdate: (updates: Partial<UserProfile>, requiresConfirmation?: boolean) => void;
  onNavigateToTab: (tabId: string) => void;
  onClose?: () => void;
}

interface PrerequisiteItem {
  key: string;
  label: string;
  navigateTo: string;
}

interface PrerequisitesCheck {
  isReady: boolean;
  missing: PrerequisiteItem[];
}

/** Response from apply endpoint */
interface ApplyPlanResponse {
  workoutsCreated: number;
  trainingPlan: TrainingPlan;
}

interface AIGeneratedSession {
  date: string;
  dayOfWeek: string;
  sessionType: string;
  sport: 'bike' | 'run' | 'rest';
  durationMin: number;
  predictedTSS: number;
  intensity: 'recovery' | 'endurance' | 'tempo' | 'threshold' | 'vo2max';
  indoor: boolean;
  intensityNotes: string;
  note?: string; // Session-level explanation
}

interface AIGeneratedWeek {
  weekNumber: number;
  weekStartDate: string;
  weekEndDate: string;
  targetTSS: number;
  actualTSS: number;
  sessions: AIGeneratedSession[];
  note?: string; // Week-level explanation
}

interface ScheduleAgentResponse {
  planStartDate: string;
  planEndDate: string;
  totalWeeks: number;
  note?: string; // Plan-level explanation (overall training philosophy)
  weeks: AIGeneratedWeek[];
  summary: {
    totalTSS: number;
    avgWeeklyTSS: number;
    totalSessions: number;
    restDays: number;
  };
}

// Loading messages that cycle during generation
const LOADING_MESSAGES = [
  'Analyzing your training history...',
  'Calculating optimal training load...',
  'Balancing intensity and recovery...',
  'Considering your schedule preferences...',
  'Optimizing for your goal...',
  'Fine-tuning workout intensities...',
  'Building your personalized plan...',
  'Almost there...',
];

const AICoachTab: React.FC<AICoachTabProps> = ({ profile, onUpdate, onNavigateToTab, onClose }) => {
  const [prerequisites, setPrerequisites] = useState<PrerequisitesCheck | null>(null);
  const [isCheckingPrereqs, setIsCheckingPrereqs] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [generatedPlan, setGeneratedPlan] = useState<ScheduleAgentResponse | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMockPlan, setIsMockPlan] = useState(false);
  // Local state for scheduler notes - only saved when generating plan
  const [schedulerNotes, setSchedulerNotes] = useState(profile.schedulerNotes || '');

  // Cycle loading messages during generation
  useEffect(() => {
    if (!isGenerating) {
      setLoadingMessageIndex(0);
      return;
    }

    const interval = setInterval(() => {
      setLoadingMessageIndex(prev => (prev + 1) % LOADING_MESSAGES.length);
    }, 3000); // Change message every 3 seconds

    return () => clearInterval(interval);
  }, [isGenerating]);

  // Check prerequisites on mount
  const checkPrerequisites = useCallback(async () => {
    setIsCheckingPrereqs(true);
    try {
      const response = await apiClient.get<PrerequisitesCheck>(
        '/api/ai-agents/schedule-agent/prerequisites'
      );
      if (response.success && response.data) {
        setPrerequisites(response.data);
      }
    } catch (err) {
      logger.error('Failed to check prerequisites', err as Error);
      setError('Failed to check prerequisites');
    } finally {
      setIsCheckingPrereqs(false);
    }
  }, []);

  useEffect(() => {
    checkPrerequisites();
  }, [checkPrerequisites]);

  // Handle plan generation
  const handleGeneratePlan = async () => {
    setIsGenerating(true);
    setError(null);

    try {
      // Save scheduler notes to profile before generating (if changed)
      if (schedulerNotes !== profile.schedulerNotes) {
        await onUpdate({ schedulerNotes });
      }

      // Use longer timeout (150s) for AI agent call
      const response = await apiClient.post<ScheduleAgentResponse>(
        '/api/ai-agents/schedule-agent/generate',
        undefined,
        { timeout: 150000 }
      );

      if (response.success && response.data) {
        setGeneratedPlan(response.data);
        setIsMockPlan(response.isMock === true);
        setShowPreview(true);
        logger.info('Training plan generated', {
          weeks: response.data.totalWeeks,
          sessions: response.data.summary.totalSessions,
          isMock: response.isMock,
        });
      } else {
        setError(response.message || 'Failed to generate plan');
      }
    } catch (err) {
      logger.error('Plan generation failed', err as Error);
      setError('Failed to generate training plan. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle plan confirmation
  const handleConfirmPlan = async () => {
    if (!generatedPlan) return;

    try {
      const response = await apiClient.post<ApplyPlanResponse>(
        '/api/ai-agents/schedule-agent/apply',
        {
          plan: generatedPlan,
        }
      );

      if (response.success && response.data) {
        setShowPreview(false);
        setGeneratedPlan(null);

        // Dispatch event with the saved training plan so Dashboard can update immediately
        window.dispatchEvent(
          new CustomEvent('training-plan-updated', {
            detail: response.data.trainingPlan,
          })
        );

        // Close the profile drawer after applying the plan
        onClose?.();

        logger.info('Training plan applied', {
          workouts: response.data.workoutsCreated,
          totalDistance: getTotalPlannedDistance(response.data.trainingPlan),
        });
      } else {
        setError(response.message || 'Failed to apply plan');
      }
    } catch (err) {
      logger.error('Failed to apply plan', err as Error);
      setError('Failed to apply training plan');
    }
  };

  // Handle cancel
  const handleCancelPreview = () => {
    setShowPreview(false);
    setGeneratedPlan(null);
  };

  // Map tab navigation
  const handleNavigate = (tabId: string) => {
    onNavigateToTab(tabId);
  };

  // Render prerequisites checklist
  const renderPrerequisites = () => {
    if (isCheckingPrereqs) {
      return (
        <Box display="flex" alignItems="center" gap={1} py={2}>
          <CircularProgress size={20} />
          <Typography variant="body2">Checking prerequisites...</Typography>
        </Box>
      );
    }

    if (!prerequisites) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load prerequisites. Please refresh and try again.
        </Alert>
      );
    }

    const allItems = [
      { key: 'trainingGoal', label: 'Training Goal', navigateTo: 'goals' },
      { key: 'weeklyTSSTarget', label: 'Weekly TSS Target', navigateTo: 'performance' },
      { key: 'schedule', label: 'Training Schedule', navigateTo: 'schedule' },
      { key: 'intervals', label: 'Intervals.icu Connection', navigateTo: 'sync' },
    ];

    // Add conditional items based on sports
    const hasRunning = Object.values(profile.weeklyWorkoutPreferences).some(prefs =>
      Array.isArray(prefs)
        ? prefs.some(p => p === 'base run' || p === 'long run' || p === 'hard run')
        : prefs === 'base run' || prefs === 'long run' || prefs === 'hard run'
    );
    const hasCycling = Object.values(profile.weeklyWorkoutPreferences).some(prefs =>
      Array.isArray(prefs)
        ? prefs.some(p => p === 'base ride' || p === 'long ride' || p === 'hard ride')
        : prefs === 'base ride' || prefs === 'long ride' || prefs === 'hard ride'
    );

    if (hasRunning) {
      allItems.push({ key: 'thresholdPace', label: 'Threshold Pace', navigateTo: 'performance' });
    }
    if (hasCycling) {
      allItems.push({ key: 'ftp', label: 'FTP (Cycling Power)', navigateTo: 'performance' });
    }

    const missingKeys = new Set(prerequisites.missing.map(m => m.key));

    return (
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Prerequisites
          </Typography>
          <List dense disablePadding>
            {allItems.map(item => {
              const isMissing = missingKeys.has(item.key);
              return (
                <ListItem
                  key={item.key}
                  disablePadding
                  sx={{ py: 0.5 }}
                  secondaryAction={
                    isMissing ? (
                      <Button
                        size="small"
                        endIcon={<NavIcon />}
                        onClick={() => handleNavigate(item.navigateTo)}
                        aria-label={`Go to ${item.label}`}
                      >
                        Set up
                      </Button>
                    ) : null
                  }
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {isMissing ? (
                      <CancelIcon color="error" fontSize="small" />
                    ) : (
                      <CheckIcon color="success" fontSize="small" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      variant: 'body2',
                      color: isMissing ? 'error.main' : 'text.primary',
                    }}
                  />
                </ListItem>
              );
            })}
          </List>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      {/* Section Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          🤖 AI Coach
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Generate an AI-powered training plan based on your goals, schedule, and current fitness.
        </Typography>
      </Box>

      {/* Error display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Prerequisites checklist */}
      {renderPrerequisites()}

      {/* Goal summary */}
      {profile.trainingGoal && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent sx={{ py: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Current Goal
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {profile.trainingGoal.goal}
            </Typography>
            {profile.trainingGoal.targetMetric && (
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    fontWeight: 600,
                  }}
                >
                  {profile.trainingGoal.targetMetric.display ||
                    `${profile.trainingGoal.targetMetric.value} ${profile.trainingGoal.targetMetric.unit}`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ({profile.trainingGoal.targetMetric.metric})
                </Typography>
              </Box>
            )}
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="caption" color="text.secondary">
                📅 Target: {profile.trainingGoal.targetDate}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color:
                    profile.trainingGoal.confidenceLabel === 'high'
                      ? 'success.main'
                      : profile.trainingGoal.confidenceLabel === 'medium'
                        ? 'warning.main'
                        : 'error.main',
                }}
              >
                🎯 {profile.trainingGoal.confidenceScore}% likelihood
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Scheduler Notes */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            📝 Notes for AI Coach
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
            Optional: Mention injuries, preferences, or constraints the AI should consider
          </Typography>
          <TextField
            multiline
            rows={3}
            fullWidth
            placeholder="e.g., 'Recovering from mild knee pain - avoid high impact runs' or 'Prefer morning sessions on weekdays'"
            value={schedulerNotes}
            onChange={e => setSchedulerNotes(e.target.value)}
            inputProps={{
              'aria-label': 'Notes for AI scheduler',
              maxLength: 500,
            }}
            helperText={`${schedulerNotes.length}/500 • Saved when generating plan`}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '0.875rem',
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Generate button or loading state */}
      {isGenerating ? (
        <Card
          variant="outlined"
          sx={{
            mb: 2,
            background:
              'linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, rgba(156, 39, 176, 0.05) 100%)',
            borderColor: 'primary.light',
          }}
        >
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Box sx={{ position: 'relative', display: 'inline-flex', mb: 2 }}>
              <CircularProgress
                size={80}
                thickness={2}
                sx={{
                  color: 'primary.main',
                  animationDuration: '2s',
                }}
              />
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AIIcon sx={{ fontSize: 32, color: 'primary.main' }} />
              </Box>
            </Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              AI Coach is thinking...
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                minHeight: 24,
                transition: 'opacity 0.3s ease-in-out',
              }}
            >
              {LOADING_MESSAGES[loadingMessageIndex]}
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 2 }}>
              This may take up to 2 minutes
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          <Button
            variant="contained"
            fullWidth
            size="large"
            startIcon={<AIIcon />}
            onClick={handleGeneratePlan}
            disabled={!prerequisites?.isReady}
            aria-label="Generate AI training plan"
            sx={{ py: 1.5 }}
          >
            Generate Training Plan
          </Button>

          {!prerequisites?.isReady && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              textAlign="center"
              sx={{ mt: 1 }}
            >
              Complete all prerequisites above to generate your plan
            </Typography>
          )}
        </>
      )}

      {/* Help text */}
      <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary">
          💡 The AI Coach uses your fatigue levels from Intervals.icu, your training preferences,
          and your goal to create a personalized plan that balances adaptation with recovery.
        </Typography>
      </Box>

      {/* Plan Preview Dialog */}
      {generatedPlan && (
        <PlanPreviewDialog
          open={showPreview}
          plan={generatedPlan}
          isMock={isMockPlan}
          onConfirm={handleConfirmPlan}
          onCancel={handleCancelPreview}
        />
      )}
    </Box>
  );
};

export default AICoachTab;
